import { useEffect, type ReactElement } from "react";

import { AppShell } from "@/app/AppShell";
import { VorinChatEmbed } from "@/components/chat/VorinChatEmbed";
import { BlogArticlePage } from "@/pages/BlogArticlePage/BlogArticlePage";
import { BlogPage } from "@/pages/BlogPage/BlogPage";
import { ContactPage } from "@/pages/ContactPage/ContactPage";
import { DynamicArchivePage } from "@/pages/DynamicPages/DynamicArchivePage";
import { DynamicDetailPage } from "@/pages/DynamicPages/DynamicDetailPage";
import { OurIdentityPage } from "@/pages/OurIdentityPage/OurIdentityPage";
import { OurMissionPage } from "@/pages/OurMissionPage/OurMissionPage";
import { OurValuesPage } from "@/pages/OurValuesPage/OurValuesPage";
import { OurVisionPage } from "@/pages/OurVisionPage/OurVisionPage";
import { PlaceholderPage } from "@/pages/PlaceholderPage/PlaceholderPage";
import { PortfolioPage } from "@/pages/PortfolioPage/PortfolioPage";
import { PrivacyPolicyPage } from "@/pages/PrivacyPolicyPage/PrivacyPolicyPage";
import { ServicesPage } from "@/pages/ServicesPage/ServicesPage";
import { getVenueXPreview } from "@/data/previewContent";
import {
  applyDocumentLocale,
  getSiteLocale,
  localizeHref,
  stripLocalePrefix,
} from "@/i18n/locale";
import { localizeContent } from "@/i18n/translations";

function normalizePathname(pathname: string) {
  let normalized = pathname;

  try {
    normalized = decodeURIComponent(pathname);
  } catch {
    normalized = pathname;
  }

  if (normalized === "/") {
    return "";
  }

  return normalized.replace(/^\/+|\/+$/g, "");
}

function getSeoMeta(pathname: string) {
  if (pathname === "") {
    return {
      title: "VenueX | Events & Brand Activations",
      description:
        "VenueX brings brand identity, digital presence and live experience together under one clear and cohesive direction.",
    };
  }

  if (pathname === "our-identity") {
    return {
      title: "Who We Are | VenueX",
      description:
        "The VenueX visual language, editorial direction and premium brand system behind every touchpoint.",
    };
  }

  if (pathname === "our-mission") {
    return {
      title: "Our Purpose | VenueX",
      description:
        "How VenueX turns brand vision into digital presence, hospitality and live experiences with one consistent standard.",
    };
  }

  if (pathname === "our-vision") {
    return {
      title: "Our Vision | VenueX",
      description:
        "The long-term VenueX approach to luxury positioning, digital clarity and memorable event execution.",
    };
  }

  if (pathname === "our-values") {
    return {
      title: "Our Values | VenueX",
      description:
        "The principles that shape the VenueX standard across identity, communication, hospitality and delivery.",
    };
  }

  if (pathname === "service" || pathname === "services") {
    return {
      title: "What We Do | VenueX",
      description:
        "Explore VenueX services across branding, websites, growth systems and event experiences.",
    };
  }

  if (pathname === "portfolio") {
    return {
      title: "Selected Work | VenueX",
      description:
        "Selected VenueX work across luxury branding, digital experiences, growth systems and live events.",
    };
  }

  if (pathname === "contact") {
    return {
      title: "Start a Project | VenueX",
      description:
        "Start a conversation with VenueX about branding, digital presence, event planning or a full authored experience.",
    };
  }

  if (pathname === "privacy-policy") {
    return {
      title: "Privacy Policy | VenueX",
      description: "VenueX privacy policy and personal data handling information.",
    };
  }

  if (pathname === "blog") {
    return {
      title: "Ideas & Insights | VenueX",
      description:
        "Editorial notes, perspective and behind-the-scenes thinking from the VenueX studio.",
    };
  }

  if (pathname.startsWith("blog/")) {
    return {
      title: "Journal Article | VenueX",
      description: "A VenueX journal article exploring branding, digital presence and event storytelling.",
    };
  }

  if (pathname.startsWith("service/")) {
    return {
      title: "Service Detail | VenueX",
      description:
        "A preserved VenueX service route ready for dedicated case-study content in the new experience.",
    };
  }

  if (pathname.startsWith("service-cat/")) {
    return {
      title: "Service Category | VenueX",
      description:
        "A preserved VenueX service category route planned for the new editorial experience.",
    };
  }

  if (pathname === "venue") {
    return {
      title: "Venues | VenueX",
      description:
        "Venue-related routes preserved for VenueX migration, continuity and final content planning.",
    };
  }

  if (pathname.startsWith("venue/")) {
    return {
      title: "Venue Detail | VenueX",
      description:
        "A venue detail route preserved during the VenueX content migration process.",
    };
  }

  if (pathname === "menu") {
    return {
      title: "Menus | VenueX",
      description:
        "Menu routes preserved for VenueX migration planning and service architecture decisions.",
    };
  }

  if (pathname.startsWith("menu/")) {
    return {
      title: "Menu Detail | VenueX",
      description:
        "A preserved VenueX menu route awaiting final migration or redirect planning.",
    };
  }

  return {
    title: "VenueX",
    description:
      "VenueX luxury branding, digital presence and event experiences.",
  };
}

export default function App() {
  const preview = getVenueXPreview();
  const requestPath = preview?.path || window.location.pathname;
  const locale = getSiteLocale(requestPath);
  const pathname = normalizePathname(stripLocalePrefix(requestPath));
  const seoMeta = localizeContent(getSeoMeta(pathname), locale);
  let page: ReactElement;

  useEffect(() => {
    applyDocumentLocale(locale);
    document.title = seoMeta.title;

    let descriptionTag = document.querySelector('meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      document.head.appendChild(descriptionTag);
    }

    descriptionTag.setAttribute("content", seoMeta.description);

    const origin = window.location.origin;
    const englishPath = stripLocalePrefix(window.location.pathname);
    const alternatePaths = {
      en: englishPath,
      el: localizeHref(englishPath, "el"),
    };

    (["canonical", "alternate-en", "alternate-el"] as const).forEach((key) => {
      const selector = key === "canonical" ? 'link[rel="canonical"]' : `link[data-venuex-locale="${key}"]`;
      let link = document.head.querySelector<HTMLLinkElement>(selector);
      if (!link) {
        link = document.createElement("link");
        document.head.appendChild(link);
      }
      link.rel = key === "canonical" ? "canonical" : "alternate";
      if (key !== "canonical") {
        link.hreflang = key === "alternate-en" ? "en" : "el";
        link.dataset.venuexLocale = key;
      }
      const targetLocale = key === "alternate-el" ? "el" : key === "alternate-en" ? "en" : locale;
      link.href = `${origin}${alternatePaths[targetLocale]}`;
    });
  }, [locale, seoMeta.description, seoMeta.title]);

  if (pathname === "") {
    page = <AppShell />;
  } else if (pathname === "our-identity") {
    page = <OurIdentityPage />;
  } else if (pathname === "portfolio") {
    page = <PortfolioPage />;
  } else if (pathname.startsWith("portfolio/")) {
    page = (
      <DynamicDetailPage
        kind="portfolio"
        slug={pathname.slice("portfolio/".length)}
        currentPath={`/${pathname}/`}
      />
    );
  } else if (pathname === "contact") {
    page = <ContactPage />;
  } else if (pathname === "privacy-policy") {
    page = <PrivacyPolicyPage />;
  } else if (pathname === "our-mission") {
    page = <OurMissionPage />;
  } else if (pathname === "our-vision") {
    page = <OurVisionPage />;
  } else if (pathname === "our-values") {
    page = <OurValuesPage />;
  } else if (pathname === "blog") {
    page = <BlogPage />;
  } else if (pathname.startsWith("blog/")) {
    const blogSlug = pathname.slice("blog/".length);
    page = <BlogArticlePage slug={blogSlug} />;
  } else if (pathname === "service" || pathname === "services") {
    page = <ServicesPage />;
  } else if (pathname.startsWith("service-cat/")) {
    page = <ServicesPage />;
  } else if (pathname.startsWith("service/")) {
    page = (
      <DynamicDetailPage
        kind="service"
        slug={pathname.slice("service/".length)}
        currentPath={`/${pathname}/`}
      />
    );
  } else if (pathname === "venue") {
    page = <DynamicArchivePage kind="venue" currentPath="/venue/" />;
  } else if (pathname.startsWith("venue/")) {
    page = (
      <DynamicDetailPage
        kind="venue"
        slug={pathname.slice("venue/".length)}
        currentPath={`/${pathname}/`}
      />
    );
  } else if (pathname === "menu") {
    page = <DynamicArchivePage kind="menu" currentPath="/menu/" />;
  } else if (pathname.startsWith("menu/")) {
    page = (
      <DynamicDetailPage
        kind="menu"
        slug={pathname.slice("menu/".length)}
        currentPath={`/${pathname}/`}
      />
    );
  } else {
    page = (
      <PlaceholderPage
        eyebrow={locale === "el" ? "Σελίδα React" : "React Route"}
        title={locale === "el" ? "Η σελίδα ετοιμάζεται" : "Page In Progress"}
        intro={locale === "el"
          ? "Η διαδρομή λειτουργεί ήδη στη νέα εφαρμογή, όμως η ειδική σελίδα της δεν έχει ακόμη σχεδιαστεί."
          : "This route is now handled inside the React build, but its dedicated page has not been designed yet."}
      />
    );
  }

  return (
    <>
      {page}
      <VorinChatEmbed />
    </>
  );
}
